import {
    type TelemetryError,
    type FilmoscopeApi,
    type FetchSourceParams,
    isFetchSourceError,
    LegacySDKPlayback,
    SDKContentParams,
    RequiredParamsForAnyPlayback,
} from '@kinopoisk-int/filmoscope';

import { DeepReadonly } from '@/scenarios/legacy-player/lib/deepReadonly';
import { toArray } from '@/scenarios/legacy-player/lib/utils/array';

import {
    AudioTrack,
    DrmSystemName,
    PlaybackRateRange,
    PlayerApiError,
    PlayingState,
    TextTrack,
    VideoSize,
    VideoTrack,
    VideoType,
    FilmoscopeAdapter as FilmoscopeAdapterApi,
    TimeRange,
    SeekingPositions,
    SeekedPositions,
    FinishEventListener,
} from '../../types/instance';
import { AutoplayPolicy, FetchSourceCallback, Source, Stream } from '../../types/config';

import { AUTO_PLAY_ERROR, PLAYBACK_RATE_RANGE, PLAYER_VIDEO_TYPES_TO_LOCAL } from './constants';
import {
    transformAudioTrackToLocal,
    transformFilmoscopeSourceToLocal,
    transformLocalSourceToFilmoscope,
    transformLocalVolumeToPlayer,
    transformPlayerVolumeToLocal,
    transformStreamToLocal,
    transformTextTrackToLocal,
    transformVideoTrackToLocal,
} from './transformers';

const noop = () => {};

type FilmoscopeAdapterOptions = {
    useSDKConfig?: boolean;
};

export class FilmoscopeAdapter implements FilmoscopeAdapterApi {
    autoplayPolicy: AutoplayPolicy | undefined = 'Allowed';

    private playingContentId: string | null = null;

    private getSource: FetchSourceCallback | null = null;

    private readonly useSDKConfig: boolean;

    readonly type = 'filmoscope';

    // eslint-disable-next-line no-empty-function
    constructor(
        private instance:
            | FilmoscopeApi
            | LegacySDKPlayback<string, Partial<RequiredParamsForAnyPlayback>>,
        options?: FilmoscopeAdapterOptions
    ) {
        this.useSDKConfig = Boolean(options?.useSDKConfig);
    }

    /** @deprecated it`s only for ad planner and ad planner adapter. Don`t use it */
    get pureInstance() {
        return this.instance;
    }

    // Mute
    get isMuted(): boolean {
        return this.instance.muted;
    }

    setIsMuted(isMuted: boolean): void {
        this.instance.muted = isMuted;
    }

    onMuteChange(callback: (isMuted: boolean) => void): () => void {
        return this.instance.listen('volumechanged', () => callback(this.isMuted));
    }

    // ObjectFit
    stretchVideoToContainerSize(): void {}

    // CurrentTime
    get currentTime(): number {
        return this.instance.currentTime;
    }

    onCurrentTimeChange(callback: (currentTime: number) => void): () => void {
        return this.instance.listen(
            'timeupdate',
            () => this.currentTime && callback(this.currentTime)
        );
    }

    // Duration
    get duration(): number {
        return this.instance.duration;
    }

    onDurationChange(callback: (duration: number) => void): () => void {
        return this.onReady(() => callback(this.duration));
    }

    // Error
    get hasError(): boolean {
        return !!this.instance.error;
    }

    onError(callback: (error: PlayerApiError) => void): () => void {
        return this.instance.listen('error', (error: TelemetryError) => {
            const { name, message, code, isFatal, details, stack, category } = error;
            const originalError = isFetchSourceError(error) ? error.originalError : undefined;
            if (code !== AUTO_PLAY_ERROR) {
                callback({
                    name,
                    message,
                    code,
                    isFatal,
                    details,
                    stack,
                    vsid: this.vsid,
                    isDrmError: category === 'DRM',
                    category,
                    originalError,
                });
            }
        });
    }

    // PlayingState
    get playingState(): PlayingState {
        const isPaused = this.instance.paused;
        const isBuffering = this.instance.buffering;
        const isEnded = this.instance.ended;

        if (isBuffering) {
            return PlayingState.buffering;
        }

        if (isEnded) {
            return PlayingState.end;
        }

        if (isPaused) {
            return PlayingState.pause;
        }

        return PlayingState.play;
    }

    onPlayingStateChange(callback: (playingState: PlayingState) => void): () => void {
        const unsubscribeIsBufferingChanged = this.instance.listen('bufferingchange', () =>
            callback(this.playingState)
        );
        const unsubscribeIsEndedChanged = this.instance.listen('ended', () =>
            callback(this.playingState)
        );
        const unsubscribeIsPausedChanged = this.instance.listen('pausedchanged', () =>
            callback(this.playingState)
        );
        const unsubscribeStateChange = this.instance.listen('statechange', () =>
            callback(this.playingState)
        );

        return () => {
            unsubscribeIsBufferingChanged();
            unsubscribeIsEndedChanged();
            unsubscribeIsPausedChanged();
            unsubscribeStateChange();
        };
    }

    onFinish(callback: FinishEventListener): () => void {
        return this.instance.listen('ended', (context) => {
            if (this.instance.ended) {
                callback(context);
            }
        });
    }

    play(): void {
        if (this.instance.ready && this.instance.paused) {
            this.instance.play();
        }
    }

    pause(): void {
        this.instance.pause();
    }

    // onContentImpression
    onContentImpression(callback: () => void): () => void {
        return this.instance.listen('playbackstart', () => callback());
    }

    // startStage
    get isStarted(): boolean {
        return this.instance.ready && !this.instance.error;
    }

    get isWaitingUserPlay(): boolean {
        return this.instance.isWaitingUserPlay;
    }

    onIsStartedChange(callback: (isStarted: boolean) => void): () => void {
        return this.onPlayingStateChange(() => callback(this.isStarted));
    }

    onWaitingUserPlayChange(callback: (isWaitingUserPlay: boolean) => void): () => void {
        return this.instance.listen('waitinguserplaychanged', () =>
            callback(this.isWaitingUserPlay)
        );
    }

    onReady(callback: () => void): () => void {
        return this.instance.listen('readychanged', () => {
            if (this.instance.ready) {
                callback();
            }
        });
    }

    // Seek
    seek(second: number): void {
        this.instance.seek(second);
    }

    onSeeked(callback: (params: SeekedPositions) => void): () => void {
        return this.instance.listen('seeked', ({ oldPosition, newPosition }) => {
            const paramsWithTimeMark = {
                oldPositionSec: oldPosition,
                newPositionSec: newPosition,
            };
            callback(paramsWithTimeMark);
        });
    }

    onSeeking(callback: (params: SeekingPositions) => void): () => void {
        return this.instance.listen('seeking', ({ oldPosition, newPosition }) => {
            const paramsWithTimeMark = {
                oldPositionSec: oldPosition,
                newPositionSec: newPosition,
            };
            callback(paramsWithTimeMark);
        });
    }

    // VideoSize
    get videoSize(): VideoSize | undefined {
        const { videoHeight: height, videoWidth: width } = this.instance;

        return {
            height,
            width,
        };
    }

    onVideoSizeChange(callback: (videoSize: VideoSize | undefined) => void): () => void {
        return this.onReady(() => callback(this.videoSize));
    }

    // Volume
    get volume(): number {
        const { volume } = this.instance;

        return volume && transformPlayerVolumeToLocal(volume);
    }

    setVolume(volumeLevel: number): void {
        const playerVolume = transformLocalVolumeToPlayer(volumeLevel);

        if (playerVolume) {
            this.setIsMuted(false);
            this.instance.volume = playerVolume;
        }
    }

    onVolumeChange(callback: (volume: number) => void): () => void {
        return this.instance.listen('volumechanged', () => callback(this.volume));
    }

    // Source
    get source(): Source | undefined {
        const source = this.instance.currentSource;

        if (!source) {
            return undefined;
        }
        return transformFilmoscopeSourceToLocal(source, this.vsid);
    }

    setSource(source: Source | Source[]): void {
        if (!('setSource' in this.instance)) {
            return;
        }
        const filmoscopeSources = toArray(source).map((item) =>
            transformLocalSourceToFilmoscope(item, this.autoplayPolicy)
        );
        this.instance.setSource(filmoscopeSources);
    }

    setContent(
        contentType: string,
        payload: SDKContentParams<Partial<RequiredParamsForAnyPlayback>>
    ): void {
        if (!this.useSDKConfig) {
            return;
        }

        if ('setContent' in this.instance && this.instance.setContent) {
            this.instance.setContent(contentType, payload);
        }
    }

    setPlayingContentId(contentId: string): void {
        this.playingContentId = contentId;

        if (this.getSource) {
            this.setGetSource(this.getSource, this.playingContentId);
        }
    }

    setGetSource(getSource: FetchSourceCallback, contentId?: string): void {
        this.getSource = getSource;
        const contentIdToPlay = contentId ?? this.playingContentId;
        if ('setGetSource' in this.instance && contentIdToPlay) {
            const transformedGetSource = (params: FetchSourceParams) =>
                getSource({ ...params, contentId: contentIdToPlay }).then((source) =>
                    toArray(source).map((item) =>
                        transformLocalSourceToFilmoscope(item, this.autoplayPolicy)
                    )
                );
            this.instance.setGetSource(transformedGetSource);
        }
    }

    onSourceChange(callback: (source: Source | undefined) => void): () => void {
        return this.instance.listen('sourcechange', () => callback(this.source));
    }

    playNextSource(): void {
        this.instance.playNextSource();
    }

    // Stream
    get currentStream(): DeepReadonly<Omit<Stream, 'drmConfig'>> | undefined {
        const stream = this.instance.currentStream;
        return stream && transformStreamToLocal(stream);
    }

    onCurrentStreamChange(
        callback: (stream: DeepReadonly<Omit<Stream, 'drmConfig'>> | undefined) => void
    ): () => void {
        return this.instance.listen('streamchange', () => callback(this.currentStream));
    }

    // WatchedTime
    get watchedTime(): number {
        return this.instance.watchedTime;
    }

    onWatchedTimeChange(callback: (watchedTime: number) => void): () => void {
        return this.instance.listen('watchedtimechange', () => callback(this.instance.watchedTime));
    }

    // AudioTrack
    get audioTrack(): AudioTrack | undefined {
        const { currentAudioTrack } = this.instance;

        if (!currentAudioTrack) {
            return undefined;
        }

        return transformAudioTrackToLocal(currentAudioTrack);
    }

    get audioTracks(): ReadonlyArray<AudioTrack> | undefined {
        return this.instance.audioTracks.map(transformAudioTrackToLocal);
    }

    setAudioTrack(track: AudioTrack): void {
        this.instance.setAudioTrack(track.value);
    }

    onCurrentAudioTrackChange(callback: (audioTrack?: AudioTrack) => void): () => void {
        return this.instance.listen('audiotrackchanged', () => callback(this.audioTrack));
    }

    onAudioTracksChange(callback: (audioTracks?: ReadonlyArray<AudioTrack>) => void): () => void {
        return this.onReady(() => callback(this.audioTracks));
    }

    // TextTrack
    get textTrack(): TextTrack | undefined {
        const { currentTextTrack } = this.instance;

        if (!currentTextTrack) {
            return undefined;
        }

        return transformTextTrackToLocal(currentTextTrack);
    }

    get textTracks(): ReadonlyArray<TextTrack> | undefined {
        return this.instance.textTracks.map(transformTextTrackToLocal);
    }

    setTextTrack(track?: TextTrack): void {
        if (!track) {
            return;
        }

        if (!track.value) {
            this.instance.resetTextTrack();
            return;
        }

        this.instance.setTextTrack(track.value);
    }

    onCurrentTextTrackChange(callback: (textTrack?: TextTrack) => void): () => void {
        return this.instance.listen('texttrackchanged', () => callback(this.textTrack));
    }

    onTextTracksChange(callback: (textTracks?: ReadonlyArray<TextTrack>) => void): () => void {
        return this.instance.listen('texttrackschanged', () => callback(this.textTracks));
    }

    // Vsid
    get vsid(): string {
        return this.instance.getVsid();
    }

    // VideoTrack
    get videoTrack(): VideoTrack | undefined {
        const { currentVideoTrack } = this.instance;

        if (!currentVideoTrack) {
            return undefined;
        }

        return transformVideoTrackToLocal(currentVideoTrack, this.instance.isABREnabled);
    }

    get videoTracks(): ReadonlyArray<VideoTrack> | undefined {
        return this.instance.videoTracks.map((videoTrack) =>
            transformVideoTrackToLocal(videoTrack)
        );
    }

    onCurrentVideoTrackChange(callback: (videoTrack?: VideoTrack) => void): () => void {
        const unsubscribeAbrStatusChanged = this.instance.listen('abrstatuschanged', () =>
            callback(this.videoTrack)
        );
        const unsubscribeVideoTrackChanged = this.instance.listen('videotrackchanged', () =>
            callback(this.videoTrack)
        );

        return () => {
            unsubscribeAbrStatusChanged();
            unsubscribeVideoTrackChanged();
        };
    }

    onVideoTracksChange(callback: (videoTracks?: ReadonlyArray<VideoTrack>) => void): () => void {
        return this.onReady(() => callback(this.videoTracks));
    }

    setVideoTrack(videoTrack?: VideoTrack): void {
        if (!videoTrack) {
            this.instance.enableABR();

            return;
        }

        this.instance.setVideoTrack(videoTrack.value);
    }

    // PlaybackRate
    get playbackRate(): number | undefined {
        return this.instance.playbackRate;
    }

    get playbackRateRange(): PlaybackRateRange | undefined {
        return PLAYBACK_RATE_RANGE;
    }

    onPlaybackRateChange(callback: (playbackRate?: number) => void): () => void {
        return this.instance.listen('ratechanged', () => callback(this.playbackRate));
    }

    onPlaybackRateRangeChange(): () => void {
        return noop;
    }

    setPlaybackRate(rate: number): void {
        this.instance.playbackRate = rate;
    }

    // BufferedRange
    get bufferedRanges(): ReadonlyArray<TimeRange> | undefined {
        return this.instance.bufferedRanges;
    }

    onBufferedRangesChange(
        callback: (bufferedRanges?: ReadonlyArray<TimeRange>) => void
    ): () => void {
        return this.instance.listen('bufferedrangeschanged', () => callback(this.bufferedRanges));
    }

    // VideoType
    get videoType(): VideoType | undefined {
        const { videoType } = this.instance;

        return videoType && PLAYER_VIDEO_TYPES_TO_LOCAL[videoType];
    }

    // IsFullscreen
    get isFullscreen(): boolean {
        return false;
    }

    // Drm
    get drmSystem(): DrmSystemName | undefined {
        return this.instance.currentStream?.drmType;
    }

    get isDrmSupported(): boolean {
        return true;
    }

    // UtcStartTime
    get utcStartTime(): number | undefined {
        return this.instance.utcPosition;
    }

    onUtcStartTimeChange(callback: (utcStartTime?: number) => void): () => void {
        return this.instance.listen('readychanged', () => callback(this.utcStartTime));
    }

    // Ad
    get adState(): undefined {
        return undefined;
    }

    onAdStateChanged(): () => void {
        return noop;
    }

    onAdPodStart(): () => void {
        return noop;
    }

    onAdPodEnd(): () => void {
        return noop;
    }

    onAdPodSkip(): () => void {
        return noop;
    }

    onAdLoaded(): () => void {
        return noop;
    }

    onAdError(): () => void {
        return noop;
    }

    onAdPodClick(): () => void {
        return noop;
    }

    // Subtitles
    get currentSubtitles(): string[] | undefined {
        return this.instance.currentSubtitles;
    }

    onCurrentSubtitlesChange(callback: (subtitles?: string[]) => void): () => void {
        return this.instance.listen('subtitleschange', () => callback(this.currentSubtitles));
    }

    setNativeSubtitlesVisibility(): void {}

    // DVR
    get dvrStartTime(): number | undefined {
        return this.instance.seekRange().start;
    }

    get dvrEndTime(): number | undefined {
        return this.instance.seekRange().end;
    }

    get dvrDuration(): number | undefined {
        const { end, start } = this.instance.seekRange();

        return end - start;
    }

    get utcPosition(): number | undefined {
        return this.instance.utcPosition;
    }

    onDVRDurationChanged(callback: (duration?: number) => void): () => void {
        return this.instance.listen('seekrangeupdated', ({ start, end }) => callback(end - start));
    }

    onDVRTimeChanged(
        callback: (props: { startTime: number; endTime: number }) => void
    ): () => void {
        return this.instance.listen('seekrangeupdated', ({ start, end }) =>
            callback({ startTime: start, endTime: end })
        );
    }

    onUTCPositionChanged(callback: (duration: number) => void): () => void {
        return this.instance.listen(
            'timeupdate',
            () => this.utcPosition !== undefined && callback(this.utcPosition)
        );
    }

    // Destroy
    destroy(): void {
        this.instance.destroy();
    }

    // PictureInPicture

    get isPictureInPicture(): boolean {
        return !!this.instance.pictureInPictureWindow;
    }

    onPictureInPictureStateChanged(callback: (isPictureInPicture: boolean) => void): () => void {
        const removeEnterPictureInPictureListener = this.instance.listen(
            'enterpictureinpicture',
            () => callback(true)
        );
        const removeLeavePictureInPictureListener = this.instance.listen(
            'leavepictureinpicture',
            () => callback(false)
        );

        return () => {
            removeEnterPictureInPictureListener();
            removeLeavePictureInPictureListener();
        };
    }

    setIsPictureInPictureAllowed(isAllowed: boolean): void {
        this.instance.disablePictureInPicture = !isAllowed;
    }

    // Skin
    onCloseButtonClick(): () => void {
        return noop;
    }

    moveToContainer(container: HTMLElement): void {
        this.instance.moveToContainer(container);
    }

    logEvent(eventName: string, details?: Record<string, unknown>) {
        this.instance.logEvent(eventName, details);
    }
}
